import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import TradingChart from '../components/TradingChart';
import LivePrice from '../components/LivePrice';
import MarketTicker from '../components/MarketTicker';
import MarketOverview from '../components/MarketOverview';
import TimeframePanel from '../components/TimeframePanel';
import PositionPanel from '../components/PositionPanel';
import RightPanel from '../components/RightPanel';
import useBinanceMarketData from '../hooks/useBinanceMarketData';

const Trading: React.FC = () => {
  const [selectedMarket, setSelectedMarket] = useState('Crypto');
  const [currentPrice, setCurrentPrice] = useState('0.00');
  const [priceChange, setPriceChange] = useState('+0.00%');
  const [isPricePositive, setIsPricePositive] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState('1m');
  const [selectedChartType, setSelectedChartType] = useState('candlestick');
  const [isRightPanelVisible, setIsRightPanelVisible] = useState(false);
  // Use Binance market data hook
  const {
    isLoading: isMarketDataLoading,
    error: marketDataError,
    isConnected: isBinanceConnected,
    getMarketDataForSymbol,
  } = useBinanceMarketData();
// Default symbol is BTCUSDT
  // const [selectedSymbol, setSelectedSymbol] = useState('BTCUSDT');
  // Default symbol is ETHUSDT
  const [selectedSymbol, setSelectedSymbol] = useState('ETHUSDT');

  // Get current symbol data from Binance
  const currentSymbolData = getMarketDataForSymbol(selectedSymbol);

  // Update price display when symbol data changes
  useEffect(() => {
    if (currentSymbolData) {
      setCurrentPrice(currentSymbolData.price);
      setPriceChange(currentSymbolData.change);
      setIsPricePositive(currentSymbolData.isPositive);
    }
  }, [currentSymbolData]);

  // Simple function to toggle right panel
  const toggleRightPanel = () => {
    setIsRightPanelVisible(prev => {
      const newValue = !prev;
      return newValue;
    });
  };

  // Make the function available globally for TradingForm to call
  useEffect(() => {
    (window as any).toggleRightPanel = toggleRightPanel;
    
    // Also listen for custom events as a fallback
    const handleToggleEvent = () => {
      toggleRightPanel();
    };
    
    window.addEventListener('toggleRightPanel', handleToggleEvent);
    
    return () => {
      delete (window as any).toggleRightPanel;
      window.removeEventListener('toggleRightPanel', handleToggleEvent);
    };
  }, []);

  const handlePriceUpdate = (price: string, change: string, isPositive: boolean) => {
    // Always allow updates to flow through for real-time chart data
    // This ensures the chart can update prices in real-time
    setCurrentPrice(price);
    setPriceChange(change);
    setIsPricePositive(isPositive);
  };

  const handleSymbolSelect = (symbol: string) => {
    setSelectedSymbol(symbol);
  };

  const handleMarketChange = (market: string) => {
    setSelectedMarket(market);
  };

  const handleTimeframeChange = (timeframe: string) => {
    setSelectedTimeframe(timeframe);
  };

  const handleChartTypeChange = (chartType: string) => {
    setSelectedChartType(chartType);
  };

  const handleCloseRightPanel = () => {
    setIsRightPanelVisible(false);
  };

  // Calculate grid layout based on right panel visibility
  const getGridLayout = () => {
    if (isRightPanelVisible) {
      return 'grid-cols-1 lg:grid-cols-6 gap-6'; // 6 columns: left(1) + middle(3) + right(2)
    }
    return 'grid-cols-1 lg:grid-cols-4 gap-6'; // 4 columns: left(1) + right(3)
  };

  const getMiddleColumnSpan = () => {
    if (isRightPanelVisible) {
      return 'lg:col-span-3'; // 3 columns for middle section
    }
    return 'lg:col-span-3'; // 3 columns when no right panel
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold theme-text-primary mb-2">Trading Dashboard</h1>
          <div className="flex items-center space-x-4 text-sm theme-text-quaternary">
            <span>Market: {selectedMarket}</span>
            <span>Symbol: {selectedSymbol}</span>
            <div className="flex items-center space-x-1">
              {isBinanceConnected ? (
                <>
                  <div className="w-2 h-2 bg-[#2AB7A6] rounded-full animate-pulse"></div>
                  <span>Live Data</span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span>Offline</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className={getGridLayout()}>
          {/* Debug Info */}
          <div className="col-span-full p-2 bg-yellow-100 text-yellow-800 text-xs mb-4 rounded">
            Debug: Right Panel Visible: {isRightPanelVisible.toString()} | Grid Layout: {getGridLayout()}
            <button 
              onClick={() => {
                toggleRightPanel();
              }}
              className="ml-4 px-2 py-1 bg-blue-500 text-white rounded text-xs"
            >
              Test Toggle
            </button>
          </div>
          
          {/* Left Column - Market Data */}
          <div className="lg:col-span-1 space-y-1">
            {/* Live Price Panel */}
            <div className="card">
              <LivePrice
                selectedSymbol={selectedSymbol}
                currentPrice={currentPrice}
                priceChange={priceChange}
                isPricePositive={isPricePositive}
              />
            </div>

            {/* Market Ticker */}
            <MarketTicker
              selectedMarket={selectedMarket}
              onMarketChange={handleMarketChange}
              onSymbolSelect={handleSymbolSelect}
            />

            {/* Market Overview */}
            <MarketOverview onSymbolSelect={handleSymbolSelect} />
          </div>

          {/* Middle Column - Chart and Controls */}
          <div className={`${getMiddleColumnSpan()} space-y-6`}>
            {/* Timeframe Panel */}
            <TimeframePanel
              selectedTimeframe={selectedTimeframe}
              onTimeframeChange={handleTimeframeChange}
              selectedChartType={selectedChartType}
              onChartTypeChange={handleChartTypeChange}
            />

            {/* Chart Panel */}
            <div className="card">
              <div className="p-4 border-b theme-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <h2 className="text-xl font-semibold theme-text-primary">
                      {selectedSymbol} Chart
                    </h2>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm theme-text-quaternary">Price:</span>
                      <span className="text-lg font-bold theme-text-primary">
                        ${currentPrice}
                      </span>
                      <span
                        className={`text-sm font-medium ${
                          isPricePositive ? 'text-[#2AB7A6]' : 'text-red-400'
                        }`}
                      >
                        {priceChange}
                      </span>
                    </div>
                  </div>
                  
                  {isMarketDataLoading && (
                    <div className="flex items-center space-x-2 text-sm theme-text-quaternary">
                      <div className="w-4 h-4 border-2 border-[#2AB7A6] border-t-transparent rounded-full animate-spin"></div>
                      <span>Loading...</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="p-4">
                <TradingChart
                  symbol={selectedSymbol}
                  timeframe={selectedTimeframe}
                  chartType={selectedChartType}
                  onPriceUpdate={handlePriceUpdate}
                  isRightPanelVisible={isRightPanelVisible}
                />
              </div>
            </div>

            {/* Open Position Panel */}
            <PositionPanel
              isRightPanelVisible={isRightPanelVisible}
            />
          </div>

          {/* Right Column - Order Book and Market Depth */}
          {isRightPanelVisible && (
            <RightPanel
              symbol={selectedSymbol}
              isVisible={isRightPanelVisible}
              onClose={handleCloseRightPanel}
              selectedSymbol={selectedSymbol}
              currentPrice={currentPrice}
              priceChange={priceChange}
              isPricePositive={isPricePositive}
              priceColor={isPricePositive ? 'text-[#2AB7A6]' : 'text-red-400'}
            />
          )}
        </div>

        {/* Error Display */}
        {marketDataError && (
          <div className="mt-6 p-4 bg-red-900/20 border border-red-700 rounded-lg">
            <div className="flex items-center space-x-2 text-red-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium">Market Data Error</span>
            </div>
            <p className="mt-2 text-sm text-red-300">{marketDataError}</p>
          </div>
        )}

        {/* Connection Status */}
        <div className="mt-6 p-4 theme-bg-secondary rounded-lg theme-border border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm theme-text-quaternary">Binance Connection:</span>
              <div className="flex items-center space-x-1">
                {isBinanceConnected ? (
                  <>
                    <div className="w-2 h-2 bg-[#2AB7A6] rounded-full animate-pulse"></div>
                    <span className="text-sm text-[#2AB7A6]">Connected</span>
                  </>
                ) : (
                  <>
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="text-sm text-red-400">Disconnected</span>
                  </>
                )}
              </div>
            </div>
            
            <div className="text-xs theme-text-quaternary">
              Data updates every 1 second when connected
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Trading;
